import { Document } from '@contentful/rich-text-types';
import styled from '@emotion/styled';
import { breakpoints, colors, spacing } from '../../../../utils/styleguide';
import RichText from '../../../contentful-elements/RichText/RichText';
import GhostButton from '../../buttons/GhostButton';
import Image, { ImageProps } from '../../Image';
import SectionTitle from '../../SectionTitle';
import Typography from '../../text/Typography';
import Container from '../Container';
import StepCard from './StepCard';
import Divider from '../../Divider';
import { css } from '@emotion/react';

export const hexColors = [
  '#def9ed', // green100 with 30% opacity
  '#ccf1e1', // green200 with 30% opacity
  '#a1dfc3', // green300 with 50% opacity
  '#77d1ac', // green300 with 70% opacity
];

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding-left: 0;
  gap: ${spacing[5]}px;
  margin-top: ${spacing[5]}px;
  margin-bottom: ${spacing[10]}px;

  ${breakpoints.desktop} {
    padding-left: ${spacing[5]}px;
  }
`;

const MainContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[4]}px;
  width: 100%;

  ${breakpoints.desktop} {
    flex-direction: row;
  }
`;

const LinkWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]}px;
  min-width: fit-content;
`;

const LogoWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  row-gap: ${spacing[4]}px;
  column-gap: ${spacing[8]}px;
`;

const LogoLink = styled.a`
  align-self: center;
  box-sizing: border-box;
  object-fit: contain;
`;

const LogoImage = styled(Image)`
  height: ${spacing[9]}px;
  width: auto;
  object-fit: contain;
  filter: brightness(0) saturate(100%) invert(7%) sepia(0%) saturate(542%) hue-rotate(148deg)
    brightness(98%) contrast(101%);
  opacity: 0.7;
`;

const StepsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;

  .step-card {
    ::after {
      content: '';
      position: absolute;
      bottom: -10px;
      left: 50px;
      width: 0;
      border-style: solid;
      border-width: 0 0 30px 30px;
      z-index: 1;
      border-color: transparent;
      transform: rotate(45deg);
    }
  }

  .step-card-0 {
    ::after {
      border-color: transparent transparent ${hexColors[0]} transparent;
    }
  }

  .step-card-1 {
    ::after {
      border-color: transparent transparent ${hexColors[1]} transparent;
    }
  }

  .step-card-2 {
    ::after {
      border-color: transparent transparent ${hexColors[2]} transparent;
    }
  }

  .step-card:last-child {
    ::after {
      display: none;
    }
  }

  ${breakpoints.desktop} {
    flex-direction: row;
    width: 100%;

    .step-card {
      ::after {
        bottom: auto;
        left: auto;
        top: 35px;
        right: -10px;
        width: 0;
        transform: rotate(-45deg);
      }
    }

    .step-card-1 {
      ::after {
        display: none;
      }
    }
  }
`;

type LogoProps = {
  link?: string;
  logoUrl?: string;
  fileName?: string;
  alt?: string;
};

const Logo = ({ link, logoUrl, fileName, alt }: LogoProps) => {
  if (!logoUrl) {
    return null;
  }

  return (
    <LogoLink href={link}>
      <LogoImage url={logoUrl} fileName={fileName} alt={alt ?? ''} />
    </LogoLink>
  );
};

type Props = {
  sectionTitle?: string;
  title?: string;
  text?: Document | string;
  logosList?: {
    link?: string;
    image?: ImageProps;
  }[];
  steps?: { title?: string; description?: Document }[];
  links?: { label?: string; href?: string }[];
};

export default function OurApproachLayout({
  sectionTitle,
  title,
  text,
  logosList,
  steps,
  links,
}: Props) {
  if (!sectionTitle && !text && !logosList?.length && !steps?.length) return null;

  return (
    <Container>
      {sectionTitle && <SectionTitle title={sectionTitle} />}

      <ContentWrapper>
        {title && <Typography variant="h3Bold">{title}</Typography>}
        <MainContentWrapper>
          {typeof text === 'string' ? (
            <Typography
              color={colors.blackSecondary}
              css={css`
                white-space: pre-wrap;
              `}
            >
              {text}
            </Typography>
          ) : (
            <RichText document={text} withCheckmark />
          )}
          {text && links?.length && <Divider vertical color={colors.gray} hideOnSmallDevice />}
          {links && (
            <LinkWrapper>
              <Typography variant="overline" color={colors.blackSecondary}>
                Read more here
              </Typography>
              {links.map(({ label, href }, index) => (
                <GhostButton
                  key={index}
                  label={label}
                  trailingIconName="chevron-right"
                  color={colors.purple400}
                  hideUnderline
                  onClick={() => window.open(href ?? undefined, '_blank')}
                />
              ))}
            </LinkWrapper>
          )}
        </MainContentWrapper>
        {!!logosList?.length && (
          <LogoWrapper>
            {logosList.map((logo, index) => (
              <Logo
                key={index}
                link={logo.link}
                logoUrl={logo.image?.url}
                fileName={logo.image?.fileName}
                alt={logo.image?.alt}
              />
            ))}
          </LogoWrapper>
        )}
      </ContentWrapper>

      {!!steps?.length && (
        <StepsWrapper>
          {steps.map(({ title, description }, index) => (
            <StepCard key={index} index={index}>
              <Typography variant="h5">{title}</Typography>
              <RichText document={description} withCheckmark />
            </StepCard>
          ))}
        </StepsWrapper>
      )}
    </Container>
  );
}
